import React, { useEffect, useState } from "react";
import { Button, CustomInput, Input, Label, Modal, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorToaster, successToaster } from "components/common/toast";
import { AddClosureAPI, SaveClosureAPI } from "network/bookings";
import { errText } from "./errText";
import { alignDates } from "./settingsLogic";

// Giorno speciale: aperto o chiuso, per tutti i servizi o un turno, con messaggio per il cliente.
const today = () => new Date().toLocaleDateString("sv-SE");
const blank = () => ({ reason: "", date_from: today(), date_to: today(), shift: "", is_open: false, message: "" });

const SpecialDayModal = ({ isOpen, closure, shifts, bandLabel, onClose, onSaved }) => {
  const { t } = useTranslation("bookings");
  const [f, setF] = useState(blank());
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setTried(false);
    setF(closure ? { ...blank(), ...closure, shift: closure.shift || "", message: closure.message || "" } : blank());
  }, [isOpen, closure]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const nameMissing = !f.reason.trim();

  const save = async () => {
    setTried(true);
    if (nameMissing || !f.date_from) return;
    setBusy(true);
    try {
      const body = {
        reason: f.reason.trim(), date_from: f.date_from, date_to: f.date_to || f.date_from,
        shift: f.shift || null, is_open: f.is_open, message: f.message.trim(),
      };
      await (closure ? SaveClosureAPI(closure.id, body) : AddClosureAPI(body));
      successToaster(t("settings.specialSaved", { name: body.reason }));
      onSaved();
    } catch (e) {
      ErrorToaster(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      {/* intestazione come pagina: ‹ indietro · titolo · Salva */}
      <div className="modal-header d-flex align-items-center">
        <Button color="link" className="px-0 mr-2" onClick={onClose}>‹ {t("settings.back")}</Button>
        <h5 className="modal-title flex-grow-1 text-center mb-0">{t(closure ? "settings.specialEdit" : "settings.specialTitle")}</h5>
        <Button color="primary" size="sm" disabled={busy} onClick={save}>{t("save")}</Button>
      </div>
      <ModalBody>
        <Label className="font-weight-bold">{t("settings.specialName")}</Label>
        <Input maxLength={25} invalid={tried && nameMissing} value={f.reason} placeholder={t("settings.specialNamePh")} onChange={(e) => set("reason", e.target.value)} />
        <div className="d-flex">
          {tried && nameMissing && <small className="text-danger">{t("settings.errName")}</small>}
          <small className="text-muted ml-auto">{f.reason.length}/25</small>
        </div>

        <Label className="mt-3 d-block font-weight-bold">{t("settings.specialDates")}</Label>
        <div className="d-flex align-items-center">
          <span className="mr-2">{t("settings.dateFrom")}</span>
          <Input type="date" value={f.date_from} onChange={(e) => setF((x) => ({ ...x, ...alignDates(e.target.value, x.date_to) }))} />
          <span className="mx-2">{t("settings.dateTo")}</span>
          <Input type="date" min={f.date_from} value={f.date_to} onChange={(e) => setF((x) => ({ ...x, ...alignDates(x.date_from, e.target.value) }))} />
        </div>
        <small className="text-muted">{t("settings.specialDatesHint")}</small>

        <Label className="mt-3 d-block font-weight-bold">{t("settings.specialServices")}</Label>
        <div className="d-flex align-items-center flex-wrap">
          <Input type="select" value={f.shift} onChange={(e) => set("shift", e.target.value)} className="mr-3 mb-2" style={{ maxWidth: 240 }}>
            <option value="">{t("settings.allServices")}</option>
            {shifts.map((s) => <option key={s.id} value={s.id}>{bandLabel(s)} ({s.start_time} - {s.end_time || s.last_seating})</option>)}
          </Input>
          <CustomInput
            type="switch" id="special-open" className="mb-2" checked={f.is_open} onChange={(e) => set("is_open", e.target.checked)}
            label={<strong className={f.is_open ? "text-success" : "text-danger"}>{t(f.is_open ? "settings.openState" : "settings.closedState")}</strong>}
          />
        </div>
        <small className="text-muted">{t("settings.specialStateHint")}</small>

        <Label className="mt-3 d-block font-weight-bold">{t("settings.notifyMessage")}</Label>
        <Input type="textarea" rows="3" maxLength={200} value={f.message} onChange={(e) => set("message", e.target.value)} />
        <small className="text-muted">{f.message.length}/200</small>
      </ModalBody>
    </Modal>
  );
};

export default SpecialDayModal;
